import {
  MeetingInstance,
  ElogParticipant,
  Protocol,
} from "@services/meetings/types";
import { FormProps } from "./components";
import { TFunction } from "react-i18next";
import { BillingCode } from "@services/account/types";
import {
  ContentProps,
  prepareRoomOptions,
  NON_EMPTY_VALUE,
  fromDevice,
  fromRoom,
} from "./const";

export const getFields = (
  args: ContentProps & {
    meeting: MeetingInstance;
    t: TFunction;
    billingCodes: BillingCode[];
  }
): FormProps["fields"] => {
  const { onChange, participant, emptyDialOption, meeting, t, billingCodes } =
    args;
  const simpleOnChange = (changes: Partial<ElogParticipant>) =>
    onChange((old) => ({ ...old, ...changes }));

  const rooms = prepareRoomOptions(meeting.participants);
  return [
    [
      {
        label: t("From"),
        type: "date",
        onChange: (connectTime) => simpleOnChange({ connectTime }),
        value: participant.connectTime ?? null,
        required: true,
      },
      {
        label: t("To"),
        type: "date",
        onChange: (disconnectTime) => simpleOnChange({ disconnectTime }),
        value: participant.disconnectTime ?? null,
      },
    ],
    [
      {
        label: t("Protocol"),
        type: "select",
        required: true,
        onSelect: (value) => simpleOnChange({ protocol: value as Protocol }),
        options: [
          Protocol.H323,
          Protocol.PSTN,
          Protocol.H320,
          Protocol.SIP,
        ].map((value) => ({ label: value, value })),
        selected: participant.protocol ?? "",
      },
    ],
    [
      {
        label: t("Direction"),
        type: "select",
        onSelect: (value) =>
          simpleOnChange({ dialIn: value === "" ? undefined : Boolean(value) }),
        options: [
          { label: t("Dialed IN"), value: 1 },
          { label: t("Dialed OUT"), value: 0 },
        ],
        selected: participant.dialIn
          ? 1
          : participant.dialIn === false
          ? 0
          : "",
      },
    ],
    [
      {
        label: t("Address"),
        type: "text",
        onChange: (address) => simpleOnChange({ address }),
        value: participant.address ?? "",
      },
    ],
    [
      {
        label: t("Bandwidth"),
        type: "number",
        onChange: (value) => {
          onChange((old) => {
            if (value === undefined || value === "")
              return { ...old, bandwidth: undefined };
            const isValidNumber =
              !isNaN(parseInt(value)) && value.match(/^[0-9]*$/);
            let bandwidth = isValidNumber ? parseInt(value) : old.bandwidth;
            return { ...old, bandwidth };
          });
        },
        value: participant.bandwidth ?? "",
      },
    ],
    [
      {
        label: t("Room"),
        type: "select",
        onSelect: (id) => {
          const found = rooms.find(({ room }) => room.id === id);
          if (found)
            simpleOnChange({
              room: fromRoom(found.room),
              device: fromDevice(found.devices[0], found.room),
              externalId: undefined,
            });
          else simpleOnChange({ room: undefined, device: undefined });
        },
        options: [
          ...rooms.map(({ room }) => ({
            label: room.name,
            value: room.id,
          })),
          ...((rooms.externals?.length ?? 0) > 0
            ? [{ label: t("Other"), value: NON_EMPTY_VALUE }]
            : []),
        ],
        selected:
          participant.room?.id ??
          ((rooms.externals?.length ?? 0) > 0 ? NON_EMPTY_VALUE : ""),
      },
      {
        label: t("Device"),
        type: "select",
        disabled: !participant.room && !rooms.externals?.length,
        onSelect: (id) => {
          const found = rooms.find(({ devices }) =>
            devices.some((device) => device.id === id)
          );
          const device = found?.devices.find((d) => d.id === id);
          if (found && device)
            simpleOnChange({
              device: fromDevice(device, found.room),
              externalId: undefined,
            });
          else {
            const external = rooms.externals?.find((e) => e.id === id);
            if (external)
              simpleOnChange({
                externalId: external.id,
                address: external.address,
                device: undefined,
              });
          }
        },
        options: (!participant.room && !!rooms.externals?.length
          ? rooms.externals
          : rooms.find(({ room }) => room.id === participant?.room?.id)
              ?.devices ?? []
        ).map(({ id, name }) => ({ value: id, label: name })),
        selected: participant.externalId ?? participant.device?.id ?? "",
      },
    ],
    [
      {
        label: t("Billing Code"),
        type: "select",
        options: billingCodes.map(({ billingCode, description }) => ({
          label: `${billingCode} (${description})`,
          value: billingCode,
        })),
        onSelect: (value) => {
          const found = billingCodes.find(
            ({ billingCode }) => billingCode === value
          );
          if (found)
            simpleOnChange({
              billingCode: found.billingCode,
              description: found.description,
            });
        },
        selected: participant.billingCode ?? "",
      },
      {
        label: t("Quantity"),
        type: "number",
        required: Boolean(participant.billingCode),
        disabled: !Boolean(participant.billingCode),
        value: participant.quantity ?? "",
        onChange: (value) => {
          onChange((old) => {
            if (value === undefined || value === "")
              return { ...old, quantity: undefined };
            const isValidNumber =
              !isNaN(parseInt(value)) && value.match(/^[0-9]*$/);
            let quantity = isValidNumber ? parseInt(value) : old.quantity;
            return { ...old, quantity };
          });
        },
      },
    ],
  ];
};

export default getFields;
